import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { 
  MessageSquare, 
  ThumbsUp, 
  ThumbsDown, 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  ArrowRight,
  Send
} from 'lucide-react';

const UTILITY_SERVER_URL = "https://lu5zfciin5mk34fowavmhz7dt40pkhhg.lambda-url.us-east-1.on.aws"; // UPDATE TO YOUR UTILS URL

export default function EmailCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL Parameters from Email
  const briefingId = searchParams.get('briefingId');
  const userId = searchParams.get('userId');
  const initialVote = searchParams.get('vote');

  // UI States
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState('');
  const [step, setStep] = useState('feedback'); // feedback | success
  const [error, setError] = useState(null);

  useEffect(() => {
    // 1. Check if user is authenticated
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      
      // If user isn't logged in, redirect to auth but keep return path
      if (!session) {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/auth?from=${returnUrl}`);
      }
    });
  }, [navigate]);

  const handleSubmitFeedback = async (e) => {
    if (e) e.preventDefault();
    if (!comment.trim()) return setError("Please provide a brief comment.");

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${UTILITY_SERVER_URL}/api/briefing/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          briefingId: briefingId,
          comment: comment.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStep('success');
      } else {
        throw new Error(data.error || "Failed to submit feedback.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06060a] flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-500" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#06060a] flex items-center justify-center p-6 font-sans text-slate-200">
      {/* Visual background accents */}
      <div className="fixed top-[-10%] left-[-5%] w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[128px] pointer-events-none" />
      <div className="fixed bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-fuchsia-600/5 rounded-full blur-[128px] pointer-events-none" />

      <div className="w-full max-w-lg relative z-10 animate-in fade-in zoom-in duration-500">
        <div className="bg-[#0b0c0e]/80 backdrop-blur-xl border border-[#1f1f23]/50 rounded-3xl shadow-2xl overflow-hidden">
          <div className="h-1.5 w-full bg-gradient-to-r from-purple-600 to-fuchsia-600" />

          <div className="p-10 md:p-12">
            
            {step === 'feedback' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex justify-center mb-8">
                  <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center border border-red-500/20 text-red-400">
                    <ThumbsDown size={32} />
                  </div>
                </div>

                <h1 className="text-3xl font-extrabold text-white mb-3 text-center">Director Calibration</h1>
                <p className="text-slate-400 mb-8 text-center text-lg">
                  You marked this briefing as <span className="text-red-400 font-bold">off track</span>. How can I improve my guidance for this project?
                </p>

                <form onSubmit={handleSubmitFeedback} className="space-y-6">
                  <div className="relative">
                    <div className="absolute left-4 top-4 text-slate-500">
                      <MessageSquare size={20} />
                    </div>
                    <textarea
                      required
                      className="w-full bg-[#0f1114] border border-[#25262a] rounded-2xl pl-12 pr-4 py-4 text-slate-100 focus:ring-2 focus:ring-purple-600 outline-none transition-all placeholder:text-slate-600 h-40 resize-none leading-relaxed"
                      placeholder="e.g. Focus more on the database schema, I'm already done with the UI..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </div>

                  {error && (
                    <div className="flex items-start gap-3 text-red-400 bg-red-950/20 p-4 rounded-xl border border-red-900/30 text-sm">
                      <AlertCircle size={18} className="shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold rounded-2xl shadow-lg flex items-center justify-center gap-3 text-xl transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="animate-spin" size={24} />
                    ) : (
                      <>
                        <span>Submit Feedback</span>
                        <Send size={20} />
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate('/console')}
                    className="w-full text-slate-500 hover:text-white font-medium text-sm transition-colors"
                  >
                    Skip and go to Dashboard
                  </button>
                </form>
              </div>
            )}

            {step === 'success' && (
              <div className="text-center py-10 animate-in zoom-in duration-500">
                <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center border border-green-500/20 mx-auto mb-8 text-green-400">
                  <CheckCircle size={48} />
                </div>
                <h2 className="text-3xl font-black text-white mb-3">Feedback Received</h2>
                <p className="text-slate-400 text-lg mb-10">
                  Thank you. I've logged your input to adjust future briefings.
                </p>
                <button
                  onClick={() => navigate('/console')}
                  className="w-full py-4 bg-[#161618] hover:bg-[#1f1f23] text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all border border-[#25262a]"
                >
                  Return to Workspace
                  <ArrowRight size={20} />
                </button>
              </div>
            )}

          </div>
        </div>
        
        <p className="text-center text-slate-600 text-xs mt-10">
          &copy; {new Date().getFullYear()} Thrust AI Director. Proactive Alignment.
        </p>
      </div>
    </div>
  );
}
